import React  from 'react';
import { network } from '@tencent/netcheck';
import { Feature } from '@tencent/workbench';
import { EventEmitter as EventBus } from '@src/slide/infrastructure/module/independent/event-emitter';
import BaseTitleBarFeature from '@framework/workbench/common/feature/base-titlebar-feature';
import { getNowUserId, getUtype } from '@tencent/docs-multi-tools';
import { getCookie } from 'sutils';
import {
    LocalFromBegin,
    LocalFromCurrent,
    RemotePlay,
    MeetingMenuItem,
    LocalWithPresenterView,
    MobileRemoteControl,
} from '@com-pc/play-menu-item';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import i18n from '@i18next/i18n';
import { CUSTOM } from '@src/slide/infrastructure/module/dependent/config/tob/constant';
import { CLICK_FROM } from '../../constant';
import DocsConfig from '@tencent/docs-config';
import { isLogin } from '@src/js/common/utils';
import ua from '@tencent/docs-user-agent';
import { TitleBarConfig } from '@src/slide/infrastructure/module/dependent/config/config-manager-constants';
import { canIUseWindowOpenInPc } from '@src/slide/infrastructure/module/independent/agent/common';
import { LiveRoleType } from '@tencent/live-components';

export enum PlayActionEventsKeys {
    USER_CHANGE = 'userChange',
    PLAY_ACTION_ACTIVE_CHANGE  = 'playActionActiveChange'
}

export interface PlayActionEventsMap {
    [PlayActionEventsKeys.USER_CHANGE]: void;
    [PlayActionEventsKeys.PLAY_ACTION_ACTIVE_CHANGE]: boolean;
}

@Feature()
export class PlayActionFeature extends BaseTitleBarFeature {
    public event = new EventBus<PlayActionEventsMap>();

    public constructor() {
        super();
        this.registerListener();
        this.getAppServices().viewableServiceLayer.remoteControlManager.promise().then((remoteControlManager) => {
            // 获取房间信息并进入，因为关闭房间需要使用 ws 消息，所以要先进入房间创建 ws 连接
            remoteControlManager.initRoomInfoAndEnter().catch(() => {});
        });
    }

    // eslint-disable-next-line complexity
    public generatePlayContent = () => {
        const meetingRemotePlay = {
            id: 'remote-meeting',
            title: <MeetingMenuItem onClick={this.handleClickMeetingRemotePlay}/>,
            showDivider: true,
            style: {
                padding: '0',
            },
            onClick: () => {},
        };
        const remotePlay = {
            id: 'remote',
            title: <RemotePlay />,
            showDivider: true,
            subTitle: this.waitingInfo(),
            onClick: this.handleRemotePlayClick,
        };
        const presenterView = {
            id: 'presenterView',
            title: <LocalWithPresenterView
                onExposure={() => {
                    tdw({
                        opername: TDWOperationType.DOC_PPT,
                        module: TDWModuleType.PC_PLAY_PRESENTER,
                        action: TDWActionType.VIEW_START_PRESENTER,
                    });
                }}
            />,
            showDivider: true,
            style: { padding: '0' },
            onClick: this.handlePresentViewClick,
        };

        const playContent = [
            {
                id: 'begin',
                title: <LocalFromBegin/>,
                onClick: this.handleLocalPlayFromBegin,
            },
            {
                id: 'current',
                title: <LocalFromCurrent/>,
                onClick: this.handleLocalPlayFromCurrent,
            },
        ];

        // 需要打开独立窗口，若能力不具备则隐藏
        if (this.showLocalWithPresentView()) {
            playContent.push(presenterView);
        }

        if (this.showMobileRemotePlay()) {
            playContent.push({
                id: 'mobileRemoteControl',
                title: (
                    <MobileRemoteControl
                        onExposure={() => {
                            tdw({
                                opername: TDWOperationType.DOC_PPT,
                                module: TDWModuleType.MOBILE_CONTROLLER,
                                action: TDWActionType.MOBILE_CONTROLLER_FUNCTION_VIEW,
                            });
                        }}
                    />
                ),
                onClick: this.handleMobileRemotePlayClick,
            });
        }

        // 登录状态下才能发起远程演示
        if (this.showRemotePlay()) {
            playContent.push(remotePlay);
        }
        if (this.showMeetingPlay()) {
            playContent.push(meetingRemotePlay);
        }

        return playContent;
    };

    public handleLocalPlayFromBegin = (isFrom = CLICK_FROM.TITLEBAR) => {
        this.getPlayManager().playAction?.(false);
    };

    public handleLocalPlayFromCurrent = (isFrom = CLICK_FROM.TITLEBAR) => {
        this.getPlayManager().playAction?.(true);
    };

    public handleRemotePlayClick = (isFrom = CLICK_FROM.TITLEBAR) => {
        if (this.getPlayManager().hasShowRemotePlay) {
            const liveBtn = document.querySelector('#live-invite-btn') as HTMLElement;
            liveBtn?.click();
        } else {
            this.onLiveShowClicked();
            const liveBtn = document.querySelector('#live-show-btn') as HTMLElement;
            liveBtn?.click();
        }
    };

    public handleClickMeetingRemotePlay = (isFrom = CLICK_FROM.TITLEBAR) => {
        if (isFrom === CLICK_FROM.TITLEBAR) {
            // 会议上报 https://docs.qq.com/sheet/BYeyFa0J8NYX4wVI1f2dm7HZ18QPLK0Rx8si1?tab=knagjw
            tdw({
                opername: TDWOperationType.DOC_MEET_WEB,
                module: TDWModuleType.DOC_TO_MEET,
                action: TDWActionType.BEGIN_PRESENTATION_CLK,
            });
        }
        const fileId = window.clientVars?.globalPadId;
        const state = encodeURIComponent(JSON.stringify({
            action: 'jump',
            file_id: fileId,
        }));
        const { clientId, redirectUri } = this.getMeetingParams();
        window.open(`/oauth/v2/direct/authorize?client_id=${clientId}&u=${getNowUserId()}&redirect_uri=${redirectUri}&state=${state}&response_type=code`);
    };

    public handlePresentViewClick = (isFrom = CLICK_FROM.TITLEBAR) => {
        if (isFrom === CLICK_FROM.TITLEBAR) {
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.PC_PLAY_PRESENTER,
                action: TDWActionType.CLK_START_PRESENTER,
            });
        }
        this.getPlayManager().playAction?.(true, false, false, true);
    };

    public showRemotePlay(): boolean {
        return !!(this.useRemotePlay() && !this.useMeetingRemotePlay());
    }

    public showMeetingPlay(): boolean {
        return !!(this.useRemotePlay() && this.useMeetingRemotePlay());
    }

    // pwa 目前仅缓存主页面 html, 所以离线不支持打开备注视图
    public showLocalWithPresentView(): boolean {
        const showPresentView = DocsConfig.getInstance().getConfig(TitleBarConfig.presentView) ?? true;
        return !!(canIUseWindowOpenInPc() && showPresentView && network.isOnline);
    }

    protected getCustomDisabledState() {
        // 切换到历史版本时播放相关按钮置灰
        return this.getAppServices().viewableServiceLayer.userPriviledgeService.getHistoricalVersionStatus();
    }

    protected showMobileRemotePlay(): boolean {
        return !!DocsConfig.getInstance().getConfig(TitleBarConfig.mobileRemoteControl)
            && isLogin() && network.isOnline;
    }

    protected handleMobileRemotePlayClick = (isFrom = CLICK_FROM.TITLEBAR) => {
        if (isFrom === CLICK_FROM.TITLEBAR) {
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.MOBILE_CONTROLLER,
                action: TDWActionType.MOBILE_CONTROLLER_FUNCTION_CLK,
            });
        } else if (isFrom === CLICK_FROM.TOOLBAR) {
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.MOBILE_CONTROLLER,
                action: TDWActionType.MOBILE_CONTROLLER_DOUBLE_TOOLBAR_FUNCTION_CLK,
            });
        }
        this.getAppServices().viewableServiceLayer.mobileRemoteControlDropdown.promise()
            .then(mobileRemoteControlDropdown => mobileRemoteControlDropdown.open());
    };

    private useRemotePlay = () => network.isOnline
        && isLogin()
        && (DocsConfig.getInstance().getConfig(TitleBarConfig.remotePlay) ?? true);

    private useMeetingRemotePlay = () => getUtype() === 'wx'
        // 微信PC小程序 也需要遵循小程序业务限制 https://iwiki.woa.com/pages/viewpage.action?pageId=588052581
        // <会议远程演示>有两个问题： 1. 跳转链接未加白 2. 最终引流到会议 app； 因此需要禁用入口
        && !ua.isWxMiniProgram && (DocsConfig.getInstance().getConfig(TitleBarConfig.remoteMeetingPlay) ?? true);


    private waitingInfo = () => {
        const liveManager = this.getAppServices().viewableServiceLayer.liveManager.dangerousInstance;
        if (!liveManager) {
            return null;
        }
        if (!this.getPlayManager().hasShowRemotePlay) return null;
        if (liveManager.role !== LiveRoleType.Master) return null;

        const { memberCount } = liveManager;
        const audienceCount = memberCount > 0 ? memberCount - 1 : 0;
        if (audienceCount > 0) {
            return i18n.t('{0}人正在等待观看', { 0: audienceCount });
        }
        return i18n.t('等待收看者加入');
    };

    private onLiveShowClicked = () => {
        (window.document.activeElement as HTMLElement)?.blur();
        tdw({
            opername: 'doc_ppt',
            module: 'remote_initiate',
            action: 'edit_clk',
        });
    };

    private getMeetingParams = () => {
        const meetingEnv = this.getMeetingEnv();
        const meetingParamsMap = {
            test: {
                clientId: 'meeting_test',
                redirectUri: CUSTOM.MEETING.TEST,
            },
            prerelease: {
                clientId: 'meeting_test',
                redirectUri: CUSTOM.MEETING.PRERELEASE,
            },
            release: {
                clientId: 'meeting_release',
                redirectUri: CUSTOM.MEETING.RELEASE,
            },
        };
        return meetingParamsMap[meetingEnv];
    };

    private getMeetingEnv = () => {
        const envName = getCookie('env_name');
        if (!envName || /^release/.test(envName)) {
            return 'release';
        }
        if (/prerelease/.test(envName)) {
            return 'prerelease';
        }
        return 'test';
    };

    private registerListener = async () => {
        const liveManager = await this.getAppServices().viewableServiceLayer.liveManager.promise();
        liveManager.event.on('UserChanged', () => {
            this.event.emit(PlayActionEventsKeys.USER_CHANGE);
        });
        this.event.on(PlayActionEventsKeys.PLAY_ACTION_ACTIVE_CHANGE, (show) => {
            this.getPlayManager().hasShowRemotePlay = show;
            // FIXME: 借用`USER_CHANGE`事件触发 UI层更新
            // 这里的本质问题是 feature/ui 的通信无法借助数据流动（目前来看）
            // TODO：
            // 1. PlayAction 整体改造为 workbench
            // 2. 数据驱动方式
            this.event.emit(PlayActionEventsKeys.USER_CHANGE);
        });
    };

    private getPlayManager() {
        return this.getAppServices().viewableServiceLayer.playManager;
    }
}
